import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Flame } from "lucide-react";
import api from "@/lib/api";

/**
 * Compact login-streak indicator for the sidebar header.
 * Renders a flame icon + day count. Links to /achievements so tapping it
 * lands you on the streak-heavy page where the number lives in context.
 *
 * Colors ramp with streak length so members see visual reinforcement of
 * their momentum without needing a badge unlock.
 */
export default function StreakFlame({ compact = false }) {
  const [streak, setStreak] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/achievements/streak");
        if (alive) setStreak(data);
      } catch (_e) { /* non-fatal */ }
    })();
    return () => { alive = false; };
  }, []);

  if (!streak || streak.login_streak < 1) return null;

  const days = streak.login_streak;
  // Colors ramp with streak length — cool → warm → prestige.
  const ramp =
    days >= 30 ? "bg-purple-100 border-purple-300 text-purple-800" :
    days >= 14 ? "bg-red-100 border-red-300 text-red-800" :
    days >= 7  ? "bg-orange-100 border-orange-300 text-orange-800" :
    days >= 3  ? "bg-amber-100 border-amber-300 text-amber-800" :
                 "bg-slate-100 border-slate-300 text-slate-700";

  return (
    <Link
      to="/achievements"
      data-testid="sidebar-streak-flame"
      title={`${days}-day login streak · longest ${streak.longest_streak}`}
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest border transition-transform hover:scale-105 ${ramp}`}
    >
      <Flame size={compact ? 10 : 11} className="opacity-80" />
      <span data-testid="sidebar-streak-value">{days}d</span>
    </Link>
  );
}
